/**
 * Pointer gesture capture, for both the trackpad and the mouse.
 *
 * Two capture modes share one recorder so a sigil registered on the craft pad
 * scores the same way when it is drawn in combat:
 *
 *  - **absolute** — plain Pointer Events on a DOM surface (crafting),
 *  - **relative** — `movementX/Y` accumulated while the pointer is locked for
 *    mouselook (combat). Integrating deltas means the camera can stay locked
 *    and the pointer never escapes the window mid-duel.
 *
 * Normalisation in @spell/gesture-engine removes position and scale, so the two
 * modes are directly comparable.
 */
import type { GesturePoint } from '@spell/shared-types';

/** Trackpad deltas are small; this maps them into roughly pad-sized units. */
const DELTA_SCALE = 0.0042;
/** Skip samples closer than this to the previous one (noise + payload size). */
const MIN_STEP = 0.004;
const MAX_POINTS = 900;

export class GestureRecorder {
  points: GesturePoint[] = [];
  active = false;

  private stroke = 0;
  private startedAt = 0;
  private cursorX = 0;
  private cursorY = 0;
  private strokeOpen = false;

  begin() {
    this.points = [];
    this.stroke = 0;
    this.strokeOpen = false;
    this.cursorX = 0;
    this.cursorY = 0;
    this.startedAt = performance.now();
    this.active = true;
  }

  /** Absolute sample, already normalised to roughly [-1, 1] by the caller. */
  addAbsolute(x: number, y: number) {
    if (!this.active) return;
    this.push(x, y);
  }

  /** Relative sample from a pointer-locked mouse or trackpad. */
  addDelta(dx: number, dy: number) {
    if (!this.active) return;
    this.cursorX += dx * DELTA_SCALE;
    this.cursorY += dy * DELTA_SCALE;
    // Keep the virtual cursor inside a sane box so a wild swipe cannot balloon
    // the normalisation scale and flatten the whole shape.
    this.cursorX = Math.max(-2.5, Math.min(2.5, this.cursorX));
    this.cursorY = Math.max(-2.5, Math.min(2.5, this.cursorY));
    this.push(this.cursorX, this.cursorY);
  }

  private push(x: number, y: number) {
    const last = this.points[this.points.length - 1];
    if (last && last.s === this.stroke && Math.hypot(x - last.x, y - last.y) < MIN_STEP) return;
    if (this.points.length >= MAX_POINTS) return;
    this.strokeOpen = true;
    this.points.push({ x, y, t: performance.now() - this.startedAt, s: this.stroke });
  }

  /** Lift: the next samples belong to a new stroke of the same sigil. */
  endStroke() {
    if (!this.strokeOpen) return;
    this.stroke = Math.min(15, this.stroke + 1);
    this.strokeOpen = false;
  }

  finish(): GesturePoint[] {
    this.active = false;
    return this.points.slice();
  }

  cancel() {
    this.active = false;
    this.points = [];
  }

  /** Points of the current stroke only, for the wand-tip trail. */
  currentStroke(): GesturePoint[] {
    return this.points.filter((p) => p.s === this.stroke);
  }

  get strokeCount(): number {
    return this.points.length === 0 ? 0 : this.stroke + 1;
  }
}

/** The single recorder used by combat; crafting makes its own per-sample. */
export const combatRecorder = new GestureRecorder();

/**
 * Live trail shared with the 3D scene. Mutated in place, read in `useFrame`,
 * never stored in React state.
 */
export const gestureTrail = {
  /** Stroke index travels with each point so the renderer can break the line. */
  points: [] as { x: number; y: number; s: number }[],
  active: false,
  color: '#8fd8ff',
};

/**
 * Publish the whole sigil, not a tail of it.
 *
 * This used to keep only the last 96 samples, which meant a sigil of any real
 * length erased its own beginning while it was still being drawn — the player
 * could never see the shape they were being judged on. The recorder is already
 * capped at 900 points and the renderer decimates, so there is nothing to save
 * by cropping here.
 */
export function syncTrailFrom(recorder: GestureRecorder) {
  gestureTrail.active = recorder.active;
  gestureTrail.points = recorder.points.map((p) => ({ x: p.x, y: p.y, s: p.s }));
}
